import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, ChevronRight } from 'lucide-react';
import { PublicLayout, ScrollReveal, SectionTitle, Badge } from './components';
import { BLOG_POSTS } from './config';
import { SEOHead, BlogSEOHead } from './SEOHead';

// ============================================
// PÁGINA LISTADO DE BLOG
// ============================================
export const BlogPage = () => {
  return (
    <PublicLayout>
      <SEOHead page="blog" />
      {/* Header */}
      <section className="pt-24 lg:pt-32 pb-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <nav className="flex items-center gap-2 text-sm text-gray-500 mb-6">
              <Link to="/web" className="hover:text-emerald-600">Inicio</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-gray-900">Blog</span>
            </nav>
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Blog sobre catering
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl">
              Consejos, tendencias y todo lo que necesitas saber para organizar eventos corporativos exitosos.
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Posts Grid */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {BLOG_POSTS.map((post, index) => (
              <ScrollReveal key={post.id} delay={index * 100}>
                <article className="group bg-white rounded-2xl overflow-hidden border border-gray-100 hover:shadow-xl transition-all duration-300">
                  <Link to={`/web/blog/${post.slug}`}>
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src={post.image}
                        alt={post.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute top-4 left-4">
                        <Badge>{post.category}</Badge>
                      </div>
                    </div>
                    <div className="p-6">
                      <time className="text-sm text-gray-500">
                        {new Date(post.date).toLocaleDateString('es-ES', { 
                          day: 'numeric', 
                          month: 'long',
                          year: 'numeric'
                        })}
                      </time>
                      <h2 className="mt-2 text-xl font-bold text-gray-900 group-hover:text-emerald-600 transition-colors line-clamp-2">
                        {post.title}
                      </h2>
                      <p className="mt-3 text-gray-600 text-sm line-clamp-3">
                        {post.excerpt}
                      </p>
                      <div className="mt-4 flex items-center text-emerald-600 font-medium text-sm">
                        Leer artículo <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </Link>
                </article>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Newsletter */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <ScrollReveal>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              ¿Quieres recibir más consejos?
            </h2>
            <p className="text-gray-600 mb-6">
              Suscríbete a nuestra newsletter y recibe novedades sobre catering corporativo.
            </p>
            <form className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Tu email corporativo"
                className="flex-1 px-4 py-3 border border-gray-300 rounded-full focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
              />
              <button
                type="submit"
                className="px-6 py-3 bg-emerald-600 text-white font-semibold rounded-full hover:bg-emerald-700 transition-colors"
              >
                Suscribirme
              </button>
            </form>
          </ScrollReveal>
        </div>
      </section>
    </PublicLayout>
  );
};

// ============================================
// PÁGINA DE ARTÍCULO INDIVIDUAL
// ============================================
export const BlogPostPage = () => {
  const { slug } = useParams();
  
  // Buscar el post por slug
  const post = BLOG_POSTS.find(p => p.slug === slug);
  
  // Si no se encuentra el post, mostrar placeholder
  if (!post) {
    return (
      <PublicLayout>
        <SEOHead page="blog" />
        <article className="pt-24 lg:pt-32 pb-16">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <ScrollReveal>
              <nav className="flex items-center gap-2 text-sm text-gray-500 mb-6">
                <Link to="/web" className="hover:text-emerald-600">Inicio</Link>
                <ChevronRight className="w-4 h-4" />
                <Link to="/web/blog" className="hover:text-emerald-600">Blog</Link>
                <ChevronRight className="w-4 h-4" />
                <span className="text-gray-900">Artículo</span>
              </nav>
            </ScrollReveal>

            <ScrollReveal delay={100}>
              <header className="mb-8">
                <Badge>Tendencias</Badge>
                <h1 className="mt-4 text-3xl lg:text-4xl font-bold text-gray-900">
                  Artículo no encontrado
                </h1>
              </header>
            </ScrollReveal>

            <ScrollReveal delay={200}>
              <div className="prose prose-lg max-w-none">
                <p className="text-gray-600">
                  Lo sentimos, no hemos encontrado el artículo que buscas.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={300}>
              <div className="mt-12">
                <Link
                  to="/web/blog"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white font-semibold rounded-full hover:bg-emerald-700 transition-colors"
                >
                  Ver todos los artículos <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </ScrollReveal>
          </div>
        </article>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <BlogSEOHead post={post} />
      <article className="pt-24 lg:pt-32 pb-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <nav className="flex items-center gap-2 text-sm text-gray-500 mb-6">
              <Link to="/web" className="hover:text-emerald-600">Inicio</Link>
              <ChevronRight className="w-4 h-4" />
              <Link to="/web/blog" className="hover:text-emerald-600">Blog</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-gray-900">{post.category}</span>
            </nav>
          </ScrollReveal>

          <ScrollReveal delay={100}>
            <header className="mb-8">
              <Badge>{post.category}</Badge>
              <h1 className="mt-4 text-3xl lg:text-4xl font-bold text-gray-900">
                {post.title}
              </h1>
              <div className="mt-4 flex items-center gap-4 text-sm text-gray-500">
                <time dateTime={post.date}>
                  {new Date(post.date).toLocaleDateString('es-ES', { 
                    day: 'numeric', 
                    month: 'long',
                    year: 'numeric'
                  })}
                </time>
                <span>•</span>
                <span>{post.readTime} lectura</span>
              </div>
            </header>
          </ScrollReveal>

          {/* Imagen destacada */}
          <ScrollReveal delay={150}>
            <div className="mb-8 rounded-2xl overflow-hidden">
              <img 
                src={post.image} 
                alt={post.title}
                className="w-full h-64 md:h-80 object-cover"
              />
            </div>
          </ScrollReveal>

          <ScrollReveal delay={200}>
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-600 text-lg leading-relaxed">
                {post.excerpt}
              </p>
              <p className="text-gray-600">
                Este artículo está siendo migrado desde nuestro blog anterior. 
                Pronto estará disponible con todo el contenido completo.
              </p>
              <p className="text-gray-600">
                Mientras tanto, puedes contactarnos para cualquier consulta sobre 
                catering para tu evento corporativo.
              </p>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={300}>
            <div className="mt-12 p-6 bg-emerald-50 rounded-2xl">
              <h3 className="font-semibold text-emerald-900 mb-2">
                ¿Organizas un evento?
              </h3>
              <p className="text-emerald-700 text-sm mb-4">
                Te ayudamos a elegir el catering perfecto para tu empresa.
              </p>
              <Link
                to="/web/contacto"
                className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white font-semibold rounded-full hover:bg-emerald-700 transition-colors"
              >
                Pedir presupuesto <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </article>
    </PublicLayout>
  );
};

export default BlogPage;
